import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, Check, Palette, DollarSign, Users, Flame } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import GoogleLoginButton from '../components/GoogleLoginButton';

function SignupPage() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const passwordStrength = () => {
    if (password.length === 0) return { level: 0, text: '', color: '' };
    if (password.length < 6) return { level: 1, text: 'Too short', color: 'bg-red-500' };
    if (password.length < 8) return { level: 2, text: 'Weak', color: 'bg-orange-500' };
    if (password.length < 12 && /[A-Z]/.test(password) && /[0-9]/.test(password)) return { level: 3, text: 'Good', color: 'bg-yellow-500' };
    if (/[A-Z]/.test(password) && /[0-9]/.test(password) && /[^A-Za-z0-9]/.test(password)) return { level: 4, text: 'Strong', color: 'bg-green-500' };
    return { level: 2, text: 'Moderate', color: 'bg-yellow-500' };
  };

  const strength = passwordStrength();

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!agreedToTerms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }
    
    setLoading(true);
    
    try {
      await signup(email, password, username);
      toast.success('Account created! Choose your plan to get started.');
      navigate('/pricing');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  }

  const features = [
    { icon: Palette, text: 'Create & publish comics' },
    { icon: DollarSign, text: 'Earn from your work' },
    { icon: Users, text: 'Join creator community' },
  ];

  return (
    <div className="min-h-screen flex" data-testid="signup-page">
      {/* Left Side - Visual */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-[var(--secondary)] to-[var(--primary)] items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/3 right-1/4 w-48 h-48 border border-white rounded-full" />
          <div className="absolute bottom-1/3 left-1/4 w-72 h-72 border border-white rounded-full" />
        </div>
        
        <div className="relative z-10 text-white p-12 max-w-lg">
          <h2 className="text-5xl font-teko font-semibold mb-6">Start your creative journey</h2>
          <p className="text-white/80 text-lg mb-8">
            Join Supremics and become part of the largest webcomic community. 
            Share your stories with millions of readers worldwide.
          </p>
          
          <div className="space-y-4">
            {features.map((feature) => (
              <div key={`feature-${feature.text}`} className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <feature.icon size={20} />
                </div>
                <span className="text-lg">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="inline-flex items-center gap-2 mb-12">
            <Flame size={32} className="text-[#E63946]" />
            <span className="text-4xl font-teko font-semibold text-gradient">Supremics</span>
          </Link>
          
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-teko font-semibold mb-2">Create an account</h1>
            <p className="text-[var(--text-secondary)]">
              Get started with your free account today
            </p>
          </div>
          
          {/* Google Sign Up */}
          <div className="mb-6">
            <GoogleLoginButton mode="signup" />
          </div>
          
          {/* Divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-[var(--border)]" />
            <span className="text-sm text-[var(--text-secondary)]">or sign up with email</span>
            <div className="flex-1 h-px bg-[var(--border)]" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-2">Username</label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Your creative name"
                required
                className="h-12 bg-[var(--surface)] border-[var(--border)] focus:border-[var(--primary)]"
                data-testid="username-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="h-12 bg-[var(--surface)] border-[var(--border)] focus:border-[var(--primary)]"
                data-testid="email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="h-12 bg-[var(--surface)] border-[var(--border)] focus:border-[var(--primary)] pr-12"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--primary)] transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {/* Password Strength */}
              {password.length > 0 && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          i <= strength.level ? strength.color : 'bg-[var(--border)]'
                        }`}
                      />
                    ))}
                  </div>
                  <p className={`text-xs ${strength.level < 3 ? 'text-red-500' : 'text-green-500'}`}>
                    {strength.text}
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Confirm Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="h-12 bg-[var(--surface)] border-[var(--border)] focus:border-[var(--primary)] pr-12"
                  data-testid="confirm-password-input"
                />
                {confirmPassword && password === confirmPassword && (
                  <Check size={20} className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500" />
                )}
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="w-4 h-4 mt-1 rounded border-[var(--border)]"
              />
              <span className="text-sm text-[var(--text-secondary)]">
                I agree to the{' '}
                <Link to="/terms" className="text-[var(--primary)] hover:underline">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-[var(--primary)] hover:underline">Privacy Policy</Link>
              </span>
            </label>

            <button
              type="submit"
              disabled={loading || !agreedToTerms}
              className="w-full h-12 bg-[var(--primary)] hover:bg-[var(--primary)]/90 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              data-testid="signup-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Create Account
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          {/* Sign In Link */}
          <p className="text-center text-[var(--text-secondary)] mt-8">
            Already have an account?{' '}
            <Link to="/login" className="text-[var(--primary)] hover:underline font-medium" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignupPage;
